import React, { useState } from "react";
import { Alert, Button, Collapse, Drawer, Empty, Spin, Typography, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { createCustomerPortalSession } from "../../../Services/billing.service";
import "./UsagePanel.css";

const { Text, Title } = Typography;

const usageLabel = (usageType, t) => t(`commercial.usage.${usageType}`, {
    defaultValue: {
        complete_analysis: "Dossiers de revue",
    }[usageType] || usageType,
});

const CONTEXTUAL_CAPABILITY_KEYS = [
    {
        key: "risk_comparison",
        capabilityKey: "risk_comparison_enabled",
        labelKey: "commercial.usage.contextualCapabilities.riskComparison",
        defaultLabel: "Comparaison orientée risque",
    },
    {
        key: "supporting_document",
        capabilityKey: "supporting_documents_for_criteria",
        labelKey: "commercial.usage.contextualCapabilities.supportingDocument",
        defaultLabel: "Documents d'appui",
    },
];

const formatDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
    });
};

const formatNumber = (value, t) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "object") {
        if (value.custom === true) {
            return t?.("commercial.usage.customLimit", { defaultValue: "Sur mesure" }) || "Sur mesure";
        }
        if (value.unlimited === true) {
            return t?.("commercial.usage.unlimited", { defaultValue: "Illimité" }) || "Illimité";
        }

        const nestedValue = value.limit ?? value.value ?? value.max ?? value.count;
        if (nestedValue !== null && nestedValue !== undefined && nestedValue !== "") {
            return String(nestedValue);
        }

        return t?.("commercial.usage.customLimit", { defaultValue: "Sur mesure" }) || "Sur mesure";
    }
    return String(value);
};

// Display-only: the backend stays the single source of truth for limits and
// remaining. We never compute entitlements here, only a visual ratio derived
// from backend-provided numbers.
const isUnlimitedUsage = (usage) =>
    !!usage && usage.unlimited === true;

const consumedRatio = (usage) => {
    if (!usage || isUnlimitedUsage(usage)) return null;
    const used = Number(usage.used || 0);
    const reserved = Number(usage.reserved || 0);
    const remaining = Number(usage.remaining || 0);
    const total = used + reserved + remaining;
    if (total <= 0) return 0;
    return Math.min(100, Math.round(((used + reserved) / total) * 100));
};

const getPeriodLabel = (plan, usageByType, t) => {
    const completeUsage = usageByType?.complete_analysis || {};
    const scope = plan?.quota_scope || completeUsage.quota_scope;
    const planCode = String(plan?.code || "").toLowerCase();

    if (scope === "lifetime" || planCode === "trial") {
        return t("commercial.usage.lifetime", { defaultValue: "Usage total" });
    }

    if (planCode === "enterprise" && completeUsage.unlimited === true) {
        return t("commercial.usage.enterprisePeriod", { defaultValue: "Selon contrat" });
    }

    const start = formatDate(completeUsage.period_start);
    const end = formatDate(completeUsage.period_end);
    if (start && end) return `${start} - ${end}`;
    return t("commercial.usage.periodUnavailable", { defaultValue: "Periode active non disponible" });
};

const isTrialLike = (plan, usageByType) => {
    const completeUsage = usageByType?.complete_analysis || {};
    const scope = plan?.quota_scope || completeUsage.quota_scope;
    const planCode = String(plan?.code || "").toLowerCase();
    return scope === "lifetime" || planCode === "trial";
};

const isInternalPlan = (plan) =>
    String(plan?.code || "").toLowerCase() === "super_admin" ||
    String(plan?.source || "").toLowerCase() === "platform_role";

const getUsageScopeNote = (plan, trialLike, t) => {
    if (plan?.cancel_at_period_end === true) {
        const endDate = formatDate(plan?.period_end);
        return t("commercial.usage.cancelScheduledScope", {
            defaultValue: endDate
                ? "Votre abonnement est résilié. L'accès reste actif jusqu'au {{date}}."
                : "Votre abonnement est résilié. L'accès reste actif jusqu'à la fin de la période payée.",
            date: endDate,
        });
    }

    const planCode = String(plan?.code || "").toLowerCase();
    if (planCode === "entrepreneur") {
        return t("commercial.usage.entrepreneurScope", {
            defaultValue: "Votre usage concerne ce dossier ponctuel et ses capacités incluses.",
        });
    }
    if (trialLike) {
        return t("commercial.usage.trialScope", {
            defaultValue: "Votre essai suit simplement les capacités incluses dans le plan Trial.",
        });
    }
    return t("commercial.usage.workspaceShared", {
        defaultValue: "Ce panneau suit les capacités disponibles sur votre plan actif.",
    });
};

const canShowUpgradeCta = (plan, onUpgrade) => {
    if (!onUpgrade || isInternalPlan(plan)) return false;
    return String(plan?.code || "").toLowerCase() !== "enterprise";
};

const subscriptionStatusLabel = (plan, t) => {
    if (plan?.cancel_at_period_end === true) {
        return t("commercial.usage.statusCancelScheduled", { defaultValue: "Résiliation programmée" });
    }
    return t("commercial.usage.statusActive", { defaultValue: "Actif" });
};

const getBillingPortalErrorMessage = (error, t) => (
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    error?.response?.data?.error ||
    error?.message ||
    t("commercial.billing.portalError", {
        defaultValue: "Impossible d'ouvrir la facturation pour le moment.",
    })
);

const UsageProgress = ({ ratio }) => {
    if (ratio === null || ratio === undefined) return null;
    return (
        <div className="commercial-usage-progress" role="presentation">
            <span
                className="commercial-usage-progress-fill"
                style={{ width: `${Math.max(4, ratio)}%` }}
                data-full={ratio >= 100 ? "true" : undefined}
            />
        </div>
    );
};

const UsageSummaryCard = ({ usageType, usage, t, className = "" }) => {
    if (!usage) return null;

    const unlimited = isUnlimitedUsage(usage);
    const ratio = consumedRatio(usage);
    const reserved = Number(usage.reserved || 0);

    return (
        <article className={`commercial-usage-card${className ? ` ${className}` : ""}`}>
            <header className="commercial-usage-card-head">
                <Text className="commercial-usage-card-title">{usageLabel(usageType, t)}</Text>
                {reserved > 0 && (
                    <span className="commercial-usage-reserved-pill">
                        {t("commercial.usage.reservedShort", {
                            defaultValue: "{{count}} en cours",
                            count: reserved,
                        })}
                    </span>
                )}
            </header>

            {unlimited ? (
                <div className="commercial-usage-card-unlimited">
                    <Text className="commercial-usage-card-unlimited-value">
                        {t("commercial.usage.unlimitedConfig", { defaultValue: "Illimité selon configuration" })}
                    </Text>
                </div>
            ) : (
                <>
                    <div className="commercial-usage-card-figures">
                        <span className="commercial-usage-card-used">{formatNumber(usage.used, t)}</span>
                        <span className="commercial-usage-card-sep">/</span>
                        <span className="commercial-usage-card-limit">{formatNumber(usage.limit, t)}</span>
                    </div>
                    <UsageProgress ratio={ratio} />
                    <Text className="commercial-usage-card-hint">
                        {t("commercial.usage.remainingShort", {
                            defaultValue: "{{count}} restant",
                            count: Number(usage.remaining || 0),
                        })}
                        {Number(usage.adjustments || 0) > 0 ? (
                            <span className="commercial-usage-card-bonus">
                                {t("commercial.usage.bonusShort", {
                                    defaultValue: " · {{count}} bonus",
                                    count: Number(usage.adjustments),
                                })}
                            </span>
                        ) : null}
                    </Text>
                </>
            )}

            {reserved > 0 && (
                <Text className="commercial-usage-card-reserved-note">
                    {t("commercial.usage.reservedNote", {
                        defaultValue: "Une analyse est en cours de finalisation.",
                    })}
                </Text>
            )}
        </article>
    );
};

const contextualCapabilityRows = (capabilities, t) => {
    const caps = capabilities || {};
    const includedText = t("commercial.usage.included", { defaultValue: "Inclus" });
    const lockedText = t("commercial.usage.locked", { defaultValue: "Verrouillé" });

    return CONTEXTUAL_CAPABILITY_KEYS
        .filter(({ capabilityKey }) => Object.prototype.hasOwnProperty.call(caps, capabilityKey))
        .map(({ key, capabilityKey, labelKey, defaultLabel }) => ({
            key,
            label: t(labelKey, { defaultValue: defaultLabel }),
            value: caps[capabilityKey] === true ? includedText : lockedText,
            locked: caps[capabilityKey] !== true,
        }));
};

const capabilityRows = (capabilities, t) => {
    const caps = capabilities || {};
    const rows = [];

    const formats = caps.allowed_upload_formats;
    if (Array.isArray(formats) && formats.length) {
        rows.push({
            key: "formats",
            label: t("commercial.usage.capabilities.formats", { defaultValue: "Formats acceptés" }),
            value: formats.map((f) => String(f).toUpperCase()).join(", "),
        });
    }

    if (caps.max_pages_per_document !== null && caps.max_pages_per_document !== undefined) {
        rows.push({
            key: "pages",
            label: t("commercial.usage.capabilities.pages", { defaultValue: "Pages couvertes par analyse" }),
            value: formatNumber(caps.max_pages_per_document, t),
        });
    }

    if (caps.max_criteria_per_analysis !== null && caps.max_criteria_per_analysis !== undefined) {
        rows.push({
            key: "criteria",
            label: t("commercial.usage.capabilities.criteria", { defaultValue: "Critères maximum par analyse" }),
            value: formatNumber(caps.max_criteria_per_analysis, t),
        });
    }

    const includedText = t("commercial.usage.included", { defaultValue: "Inclus" });
    const lockedText = t("commercial.usage.locked", { defaultValue: "Verrouillé" });

    const pushToggle = (key, hasKey, included, label) => {
        if (!hasKey) return;
        rows.push({
            key,
            label,
            value: included ? includedText : lockedText,
            locked: !included,
        });
    };

    pushToggle(
        "contract_profile",
        ("advanced_contract_profile_enabled" in caps) || ("contract_profile_advanced_locked" in caps),
        caps.advanced_contract_profile_enabled === true && caps.contract_profile_advanced_locked !== true,
        t("commercial.usage.capabilities.contract_profile", { defaultValue: "Profil du contrat avancé" }),
    );

    pushToggle(
        "memo",
        "strategic_memo_enabled" in caps,
        caps.strategic_memo_enabled === true,
        t("commercial.usage.capabilities.memo", { defaultValue: "Mémo stratégique" }),
    );

    pushToggle(
        "export",
        "standard_exports_enabled" in caps,
        caps.standard_exports_enabled === true,
        t("commercial.usage.capabilities.export", { defaultValue: "Exports" }),
    );

    pushToggle(
        "details",
        "full_comment_details_enabled" in caps,
        caps.full_comment_details_enabled === true,
        t("commercial.usage.capabilities.details", { defaultValue: "Détails complets des commentaires" }),
    );

    return rows;
};

const UsagePanel = ({
    open,
    onClose,
    usageSnapshot,
    usageByType = {},
    capabilities = {},
    plan = null,
    isLoading = false,
    error = null,
    onRefresh,
    onUpgrade,
    canManageBilling = false,
    billingReturnPath = null,
}) => {
    const { t } = useTranslation("review");
    const [billingPortalLoading, setBillingPortalLoading] = useState(false);
    const resolvedPlan = plan || usageSnapshot?.plan || null;
    const resolvedUsage = usageByType && Object.keys(usageByType).length
        ? usageByType
        : usageSnapshot?.usage || {};
    const resolvedCapabilities = capabilities && Object.keys(capabilities).length
        ? capabilities
        : usageSnapshot?.capabilities || {};
    const hasUsage = !!usageSnapshot || Object.keys(resolvedUsage).length > 0;
    const trialLike = isTrialLike(resolvedPlan, resolvedUsage);
    const internalPlan = isInternalPlan(resolvedPlan);
    const showUpgradeCta = canShowUpgradeCta(resolvedPlan, onUpgrade);
    const capRows = capabilityRows(resolvedCapabilities, t);
    const contextualRows = contextualCapabilityRows(resolvedCapabilities, t);

    const planTitle = internalPlan
        ? t("commercial.usage.superAdminTitle", { defaultValue: "SUPER ADMIN" })
        : String(resolvedPlan?.name || resolvedPlan?.code || "-").toUpperCase();
    const planCode = String(resolvedPlan?.code || "").toLowerCase();
    const usageHeading = internalPlan
        ? t("commercial.usage.platformHeading", { defaultValue: "Accès plateforme" })
        : planCode === "entrepreneur"
        ? t("commercial.usage.entrepreneurHeading", { defaultValue: "Usage du dossier" })
        : trialLike
        ? t("commercial.usage.trialHeading", { defaultValue: "Usage d'essai" })
        : t("commercial.usage.workspaceHeading", { defaultValue: "Usage du workspace" });
    const closeLabel = t("commercial.usage.close", { defaultValue: "Fermer" });
    const titleLabel = t("commercial.usage.title", { defaultValue: "Plan et usage" });
    const cancelScheduled = resolvedPlan?.cancel_at_period_end === true;

    const handleManageBilling = async () => {
        setBillingPortalLoading(true);
        try {
            await createCustomerPortalSession({ returnPath: billingReturnPath });
        } catch (billingError) {
            message.error(getBillingPortalErrorMessage(billingError, t));
            setBillingPortalLoading(false);
        }
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            width={460}
            title={(
                <div className="commercial-usage-panel-title">
                    <span className="commercial-usage-panel-title-text">{titleLabel}</span>
                    <Button
                        type="text"
                        className="commercial-usage-panel-close"
                        aria-label={closeLabel}
                        title={closeLabel}
                        icon={<CloseOutlined />}
                        onClick={onClose}
                    />
                </div>
            )}
            className="commercial-usage-panel"
            closable={false}
            destroyOnHidden
            extra={(
                <div className="commercial-usage-panel-actions">
                    {onRefresh ? (
                        <Button size="small" onClick={onRefresh} loading={isLoading}>
                            {t("commercial.usage.refresh", { defaultValue: "Actualiser" })}
                        </Button>
                    ) : null}
                </div>
            )}
        >
            {isLoading && !hasUsage ? (
                <div className="commercial-usage-loading">
                    <Spin />
                </div>
            ) : null}

            {error && (
                <Alert
                    type="warning"
                    showIcon
                    className="commercial-usage-alert"
                    message={t("commercial.usage.errorTitle", { defaultValue: "Usage indisponible" })}
                    description={error?.message?.default_body_fr || error?.message || t("commercial.usage.errorBody", {
                        defaultValue: "Impossible de charger l'usage pour le moment.",
                    })}
                />
            )}

            {!isLoading && !hasUsage ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t("commercial.usage.empty", { defaultValue: "Aucune donnee d'usage disponible." })}
                />
            ) : null}

            {hasUsage && (
                <div className="commercial-usage-content">
                    <section className="commercial-usage-plan">
                        <div className="commercial-usage-plan-top">
                            <Text className="commercial-usage-eyebrow">
                                {t("commercial.usage.planActive", { defaultValue: "Plan actif" })}
                            </Text>
                            <span className={`commercial-usage-status-pill${cancelScheduled ? " commercial-usage-status-pill--scheduled-cancel" : ""}`}>
                                {subscriptionStatusLabel(resolvedPlan, t)}
                            </span>
                        </div>
                        <Title level={4} className="commercial-usage-plan-title">
                            {planTitle}
                        </Title>
                        <Text className="commercial-usage-period">
                            {getPeriodLabel(resolvedPlan, resolvedUsage, t)}
                        </Text>
                        <Text className="commercial-usage-workspace-note">
                            {internalPlan
                                ? t("commercial.usage.platformNote", {
                                    defaultValue: "Les quotas client ne s'appliquent pas aux opérations super admin.",
                                })
                                : getUsageScopeNote(resolvedPlan, trialLike, t)}
                        </Text>
                        {showUpgradeCta && (
                            <Button
                                type="primary"
                                className="commercial-usage-upgrade-button"
                                onClick={onUpgrade}
                            >
                                {t("commercial.usage.upgrade", { defaultValue: "Voir les options" })}
                            </Button>
                        )}
                        {canManageBilling && !internalPlan && (
                            <Button
                                className="commercial-usage-billing-button"
                                onClick={handleManageBilling}
                                loading={billingPortalLoading}
                            >
                                {t("commercial.billing.manage", { defaultValue: "Gérer la facturation" })}
                            </Button>
                        )}
                    </section>

                    <section className="commercial-usage-summary">
                        <Text className="commercial-usage-section-title">
                            {usageHeading}
                        </Text>
                        <UsageSummaryCard
                            usageType="complete_analysis"
                            usage={resolvedUsage.complete_analysis}
                            t={t}
                            className="commercial-usage-card-primary"
                        />
                    </section>

                    {contextualRows.length ? (
                        <section className="commercial-usage-entitlements">
                            <Text className="commercial-usage-section-title">
                                {t("commercial.usage.contextualCapabilities.title", {
                                    defaultValue: "Capacités d'analyse",
                                })}
                            </Text>
                            <div className="commercial-usage-entitlement-list">
                                {contextualRows.map((row) => (
                                    <div key={row.key} className="commercial-usage-entitlement">
                                        <Text className="commercial-usage-entitlement-label">
                                            {row.label}
                                        </Text>
                                        <Text
                                            className={`commercial-usage-entitlement-value${row.locked ? " is-locked" : ""}`}
                                        >
                                            {row.value}
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        </section>
                    ) : null}

                    <Collapse
                        className="commercial-usage-collapse"
                        bordered={false}
                        items={[
                            {
                                key: "capabilities",
                                label: t("commercial.usage.capabilitiesTitle", { defaultValue: "Détails du plan" }),
                                children: capRows.length ? (
                                    <div className="commercial-usage-capability-list">
                                        {capRows.map((row) => (
                                            <div key={row.key} className="commercial-usage-capability">
                                                <Text className="commercial-usage-capability-label">
                                                    {row.label}
                                                </Text>
                                                <Text
                                                    className={`commercial-usage-capability-value${row.locked ? " is-locked" : ""}`}
                                                >
                                                    {row.value}
                                                </Text>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <Text className="commercial-usage-capability-empty">
                                        {t("commercial.usage.capabilitiesEmpty", { defaultValue: "Aucun détail supplémentaire." })}
                                    </Text>
                                ),
                            },
                        ]}
                    />
                </div>
            )}
        </Drawer>
    );
};

export default UsagePanel;
